import { getClassFunction } from '@helpers/functions';
import { TopBar } from '@helpers/TopBar';
import { SitePreferences } from '@helpers/SitePreferences';
import { removeVisibleClassWhenEndAnimation, setOverflowBody } from '@helpers/header-helpers';
import { currentViewport, DEVICES } from '@helpers/breakpoint';
import HeaderAnimation from '@helpers/HeaderAnimation.class';

const getClass = getClassFunction('kf-o-header');

class Header {
  header: HTMLDivElement | null;
  countrySelectorPrompt?: HTMLDivElement | null;
  burgerButton?: HTMLButtonElement | null;
  closeButton?: HTMLButtonElement | null;
  container?: HTMLDivElement | null;
  navigation?: HTMLDivElement | null;
  topBarDesktop?: HTMLDivElement | null;
  topBarMobile?: HTMLDivElement | null;
  state: 'idle' | 'main-nav' | 'sub-nav' = 'idle';
  navigationItems?: NodeListOf<HTMLDivElement> | null;
  animation: HeaderAnimation;

  constructor() {
    this.header = document.querySelector<HTMLDivElement>(getClass());
    this.countrySelectorPrompt = this.header?.querySelector<HTMLDivElement>('.kf-o-country-selector-prompt');
    this.burgerButton = this.header?.querySelector(getClass('mobile-burger'));
    this.closeButton = this.header?.querySelector(getClass('mobile-close'));
    this.container = this.header?.querySelector(getClass('container'));
    this.navigation = this.header?.querySelector(getClass('navigation'));
    this.topBarDesktop = this.header?.querySelector('.kf-o-header__top-bar-desktop .kf-o-top-bar');
    this.topBarMobile = this.header?.querySelector('.kf-o-header__top-bar-mobile .kf-o-top-bar');
    this.navigationItems = this.header?.querySelectorAll('.kf-o-header__navigation__item');
    this.animation = new HeaderAnimation({
      header: this.header,
      burgerButton: this.burgerButton,
      closeButton: this.closeButton,
      container: this.container,
      navigation: this.navigation,
      topBarDesktop: this.topBarDesktop,
      topBarMobile: this.topBarMobile,
      navigationItems: this.navigationItems,
    });
  }

  init() {
    this.initState();
    this.initHeaderObserver();
    this.addEventToMobileBurgerButton();
    this.addEventToMobileCloseButton();
    this.addEventToNavigation();
    this.handleWindowResize();
    this.initTopBar();
    this.initSitePreferences();
    this.addCustomCssVariable();
    this.addCustomEvent();
    this.animation.init();
    this.detectHeroTop();
  }

  addCustomEvent() {
    this.header?.addEventListener('close-mobile-main-nav', () => {
      if (this.header) {
        this.closeMobileMainNav(this.header);
      }
    });

    this.header?.addEventListener('close-mobile-sub-nav', () => {
      if (this.header) {
        this.closeMobileSubNav();
      }
    });
  }

  initState() {
    if (!this.header) return;

    this.header.dataset.state = this.state;
  }

  initTopBar() {
    const topBars = this.header?.querySelectorAll<HTMLDivElement>('.kf-o-top-bar');

    topBars?.forEach((topBar) => new TopBar(topBar));
  }

  initSitePreferences() {
    const sitePreferences = this.header?.querySelector<HTMLDivElement>('.kf-o-site-preferences');

    if (!sitePreferences) return;

    new SitePreferences(sitePreferences).init();
  }

  handleIdleState(target: HTMLDivElement) {
    if (currentViewport() === DEVICES.DESKTOP) {
      this.closeMobileMainNav(target);
    }

    this.header?.dispatchEvent(new CustomEvent('idle-state'));
  }

  handleOpenMobileMainNav(target: HTMLDivElement) {
    this.openMobileMainNav(target);

    // after set height, let play animation immediately after one loop
    this.header?.dispatchEvent(new CustomEvent('main-nav-state'));
  }

  openMobileMainNav(target: HTMLDivElement) {
    setOverflowBody(true);
    target.classList.add('kf-o-header--mobile-opened');
    this.topBarMobile?.classList?.add('kf-o-top-bar--mobile-opened');
  }

  closeMobileMainNav(target: HTMLDivElement) {
    target.classList.remove('kf-o-header--mobile-opened');
    this.topBarMobile?.classList?.remove('kf-o-top-bar--mobile-opened');
    this.navigation?.classList.remove('subnav-open');
  }

  observeHeaderState(mutations: MutationRecord[]) {
    const record = mutations[0];
    const target = record.target as HTMLDivElement;

    const newState = target.dataset.state;

    this.closeMobileSubNav();

    if (newState === 'main-nav') {
      this.handleOpenMobileMainNav(target);
    } else if (newState === 'idle') {
      this.handleIdleState(target);
    }
  }

  initHeaderObserver() {
    if (!this.header) return;

    const observer = new MutationObserver(this.observeHeaderState.bind(this));

    observer.observe(this.header, {
      attributeOldValue: true,
      attributeFilter: ['data-state'],
    });
  }

  openSubNav(target: HTMLDivElement) {
    if (!this.container || !this.navigation) return;
    if (!this.isDesktop()) return;
    this.closeSubNav();
    if (!target.querySelector('.kf-o-navigation-content')) return;

    this.header?.classList.add('kf-o-header--open-sub-nav');

    this.removeVisibleClass();
    target.classList.add('visible');
    target.classList.add('run-animation');
  }

  closeSubNav() {
    if (!this.isDesktop()) return;
    this.header?.classList.remove('kf-o-header--open-sub-nav');
    [].forEach.call(this.navigationItems, (navItem: HTMLDivElement) => {
      navItem.classList.remove('run-animation');
    });
    this.navigation?.classList.remove('subnav-open');
  }

  removeVisibleClass() {
    [].forEach.call(this.navigationItems, (navItem: HTMLDivElement) => {
      navItem.classList.remove('visible');
    });
  }

  openMobileSubNav(target: HTMLDivElement) {
    if (!target.querySelector('.kf-o-navigation-content')) return;
    if (currentViewport() === DEVICES.DESKTOP) return;

    target.classList.add('visible');
    this.navigation?.classList.add('subnav-open');

    // add class to animation submenu
    this.header?.dispatchEvent(
      new CustomEvent('mobile-sub-nav-opened', {
        detail: { target },
      })
    );
  }

  closeMobileSubNav() {
    if (this.isDesktop()) return;

    this.removeVisibleClass();

    this.navigation?.classList.remove('subnav-open');
  }

  isDesktop() {
    return window.innerWidth >= 1280;
  }

  addEventToMobileBurgerButton() {
    this.burgerButton?.addEventListener('click', () => this.onOpenMobileMenu());
  }

  addEventToMobileCloseButton() {
    this.closeButton?.addEventListener('click', () => this.onCloseMobileMenu());
  }

  addEventToNavigation() {
    [].forEach.call(this.navigationItems, (navItem: HTMLDivElement) => {
      let timeout: ReturnType<typeof setTimeout> | null = null;

      navItem.addEventListener('mouseenter', () => {
        timeout = setTimeout(() => {
          this.setNavItemHeight(navItem);
          this.pauseExtendAnimationDuplicate(navItem);
          this.closeSubNav();
          this.openSubNav(navItem);
          this.toggleProcessEndAnimation();
        }, 200);
      });
      navItem.addEventListener('mouseleave', () => {
        if (timeout) {
          clearTimeout(timeout);
          timeout = null;
        }
      });
      navItem.addEventListener('click', this.openMobileSubNav.bind(this, navItem));
      navItem.querySelector<HTMLButtonElement>('.kf-o-header__navigation__close')?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.onCloseMobileMenu();
        this.closeMobileSubNav();
      });

      navItem.querySelector<HTMLButtonElement>('.kf-o-header__navigation__back')?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        this.header?.dispatchEvent(
          new CustomEvent('mobile-sub-nav-close', {
            detail: { target: navItem },
          })
        );
      });
    });

    this.header?.querySelector(getClass('container'))?.addEventListener('mouseleave', () => {
      this.closeSubNav();
      this.resetNavItemHeight();
      this.resetExtendAnimationDuplicate();
    });
  }

  toggleProcessEndAnimation() {
    const activeItems = this.header?.querySelectorAll<HTMLElement>('.kf-o-header__navigation__item.visible');

    [].forEach.call(activeItems, (navItem: HTMLDivElement) => {
      navItem.querySelector<HTMLElement>('.kf-o-header__sub-header')?.addEventListener('transitionend', removeVisibleClassWhenEndAnimation);
    });
  }

  pauseExtendAnimationDuplicate(currentItem: HTMLElement) {
    if (!this.navigationItems || !this.isDesktop()) {
      return;
    }

    if (!currentItem.querySelector('.kf-o-header__sub-header')) {
      this.resetExtendAnimationDuplicate();

      return;
    }

    const hasDuplicateItemActive = Array.from(this.navigationItems).some(
      (navItem) => currentItem !== navItem && navItem.classList.contains('visible')
    );

    if (hasDuplicateItemActive) {
      Array.from(this.navigationItems).forEach((navItem) => {
        navItem.classList.add('disable-extend-animation');
      });
    }
  }

  setNavItemHeight(currentItem: HTMLElement) {
    const subHeaderContent = currentItem.querySelector<HTMLElement>('.kf-o-header__sub-header__content');

    if (!this.navigationItems || !this.isDesktop()) {
      return;
    }

    if (!subHeaderContent) {
      this.resetNavItemHeight();

      return;
    }

    this.header?.style.setProperty('--globalnav-submenu-height', `${subHeaderContent.offsetHeight}px`);
  }

  resetNavItemHeight() {
    this.header?.style.setProperty('--globalnav-submenu-height', '0px');
  }

  resetExtendAnimationDuplicate() {
    if (!this.navigationItems || !this.isDesktop()) {
      return;
    }

    Array.from(this.navigationItems).forEach((navItem) => {
      navItem.classList.remove('disable-extend-animation');
    });
  }

  handleWindowResize() {
    window.addEventListener('resize', (event) => {
      if (!this.header) return;

      const headerState = this.header?.dataset?.state;

      if (!headerState) return;

      const isMobileNavOpened = headerState === 'main-nav' || headerState === 'sub-nav';

      if ((event.target as Window).screen.width >= 1280 && isMobileNavOpened) {
        this.handleIdleState(this.header);
      }
    });
  }

  onOpenMobileMenu() {
    if (!this.header) return;

    if (!this.isDesktop()) {
      document.documentElement.style.setProperty('--header-initial-height', `${this.header.offsetHeight || 0}px`);
    }

    this.header.dataset.state = 'main-nav';
  }

  onCloseMobileMenu() {
    if (!this.header) return;
    this.header.dataset.state = 'idle';
  }

  addCustomCssVariable() {
    [].forEach.call(this.navigationItems, (navItem: HTMLElement) => {
      // content text
      const contentText = navItem.querySelectorAll<HTMLElement>(
        '.kf-o-navigation-content__text__title, .kf-o-navigation-content__text__sub-title, .kf-o-navigation-content__right-content'
      );

      this.setContentTextCssVariable(contentText);

      // group
      const groups = navItem.querySelectorAll<HTMLElement>(
        '.kf-o-navigation-content__text, .kf-o-navigation-content__links-list__item, .kf-o-navigation-content__right-content'
      );

      this.setGroupsCssVariable(groups);

      // caption item
      const linkList = navItem.querySelectorAll<HTMLElement>('.kf-o-navigation-content__links-list__item');

      this.setLinkListCssVariable(linkList, navItem);
    });
  }

  setGroupsCssVariable(groups: NodeListOf<HTMLElement>) {
    [].forEach.call(groups, (group: HTMLElement, index: number) => {
      group?.style.setProperty('--group-number', `${index + 1}`);
    });
  }

  setLinkListCssVariable(linkList: NodeListOf<HTMLElement>, navItem: HTMLElement) {
    let totalListItem = 0;

    [].forEach.call(linkList, (linkListItem: HTMLElement, index: number) => {
      linkListItem?.style.setProperty('--caption-item-number', `${index}`);

      const links = linkListItem.querySelectorAll<HTMLElement>(
        '.kf-o-navigation-content__links-list__caption, .kf-o-navigation-content__links-list__link, .kf-a-button'
      );

      this.setLinksCssVariable(links);

      navItem.style.setProperty('--total-list-item', `${totalListItem > links.length ? totalListItem : links.length}`);

      totalListItem = links.length;
    });
  }

  setLinksCssVariable(links: NodeListOf<HTMLElement>) {
    [].forEach.call(links, (link: HTMLElement, index: number) => {
      link.style.setProperty('--nav-item-number', `${index + 1}`);
    });
  }

  setContentTextCssVariable(texts: NodeListOf<HTMLElement>) {
    [].forEach.call(texts, (text: HTMLElement, index: number) => {
      text.style.setProperty('--nav-item-number', `${index + 1}`);
    });
  }

  detectHeroTop() {
    const heroEl = document.querySelector<HTMLElement>('.kf-o-hero');

    if (!heroEl) {
      return;
    }

    const setHeroTop = () => {
      const heroTop = heroEl.offsetTop;

      heroEl.style.setProperty('--hero-top', `${heroTop}px`);

      let cspHeight = 0;
      let containerHeight = 0;

      if (this.countrySelectorPrompt) {
        cspHeight = this.countrySelectorPrompt.offsetHeight;
        heroEl.style.setProperty('--country-selector-prompt-height', `${cspHeight}px`);
      }

      if (this.container) {
        containerHeight = this.container.offsetHeight;
      }

      if (this.header) {
        const headerHeight = this.header.offsetHeight;
        const isTransparent = this.header.classList.contains('kf-o-header--transparent');
        const isOverlay = this.header.classList.contains('kf-o-header--overlay');

        if (isTransparent || isOverlay) {
          heroEl.style.setProperty('--header-height', `${headerHeight - containerHeight - cspHeight}px`);
          heroEl.style.setProperty('--hero-header-overlap', `${headerHeight - containerHeight}px`);
          heroEl.style.setProperty('--hero-padding-top-lg', `${headerHeight - containerHeight - cspHeight}px`);
        } else {
          heroEl.style.setProperty('--header-height', `${headerHeight - cspHeight}px`);
        }
      }
    };

    setHeroTop();

    // Set hero top on header class change
    if (this.header) {
      const resizeObserver = new ResizeObserver(setHeroTop);

      resizeObserver.observe(this.header);
    }
  }
}

new Header().init();

export {};
